/**
 * questionText.js — PROD-184. The question TEXT travels beside the answers.
 *
 * ★★★ THE DEFECT. `questionnaire_responses` (the MD panel reads it as
 *   describe.clinicalSurvey) stored answers only, keyed by an answer key:
 *     anchor   `<category>_<questionId>`            e.g. "details_q1": ["Two days"]
 *     augment  `<category>_augment_<augQuestionId>` e.g. "details_augment_aug-details-q1": "..."
 *   The MD panel resolved the text from a questionnaire BUNDLED into its own
 *   build, but the patient app fetches questionnaires from the API at runtime and
 *   appends AI-generated augment questions that exist nowhere else — so the panel
 *   showed "Q1" and "Augment Aug-Details-Q1" instead of the question.
 *
 * ★★ THE SHAPE, and why it is this shape. The answer VALUES are unchanged (every
 *   reader in three repos reads them as arrays / strings). The text rides in ONE
 *   reserved key inside the same object:
 *
 *     questionnaire_responses = {
 *       "details_q1": ["Two days"],
 *       "details_augment_aug-details-q1": "Only at night",
 *       "_questionText": [
 *         { "key": "details_q1", "id": "q1", "category": "details",
 *           "source": "anchor",  "text": "How long have you had this?" },
 *         { "key": "details_augment_aug-details-q1", "id": "aug-details-q1",
 *           "category": "details", "source": "augment", "text": "When is it worst?" }
 *       ]
 *     }
 *
 *   · Leading underscore: the store's sanitizeData already drops `_`-keys, so the
 *     reserved key can never be re-saved as if it were an answer.
 *   · An ARRAY, not a map: the API share preview walks every dict-valued entry of
 *     questionnaire_responses as a nested category; a list is skipped there.
 *   · Only entries whose `key` is present in the answers are written.
 *   It is attached ONLY at the API boundary (withQuestionText) and stripped on the
 *   way in (stripQuestionText), so local `answers` never contains it.
 */

export const QUESTION_TEXT_KEY = '_questionText'

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v)

/** The answers without the reserved key. Non-objects are returned unchanged. */
export function stripQuestionText(responses) {
  if (!isPlainObject(responses) || !(QUESTION_TEXT_KEY in responses)) return responses
  const answers = {}
  for (const [k, v] of Object.entries(responses)) if (k !== QUESTION_TEXT_KEY) answers[k] = v
  return answers
}

/** The reserved key's entries from a server copy, validated; [] when absent. */
export function readQuestionText(responses) {
  const list = isPlainObject(responses) ? responses[QUESTION_TEXT_KEY] : null
  if (!Array.isArray(list)) return []
  return list.filter((e) => isPlainObject(e) && typeof e.key === 'string' && typeof e.text === 'string' && e.text)
}

/** Merge entry lists by `key`; later lists win. */
export function mergeQuestionText(...lists) {
  const byKey = new Map()
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const e of list) {
      if (isPlainObject(e) && typeof e.key === 'string' && typeof e.text === 'string' && e.text) byKey.set(e.key, e)
    }
  }
  return [...byKey.values()]
}

/**
 * Entries for every question the patient can be shown.
 * @param {object} questionsByCategory  focusArea.questions — { details: [{id,text}], ... }
 * @param {object} augmentByCategory    { details: [{id,text}], ... } — AI augment questions
 */
export function buildQuestionTextEntries(questionsByCategory, augmentByCategory) {
  const out = []
  if (isPlainObject(questionsByCategory)) {
    for (const [category, qs] of Object.entries(questionsByCategory)) {
      if (!Array.isArray(qs)) continue
      for (const q of qs) {
        if (q && q.id != null && typeof q.text === 'string' && q.text) {
          out.push({ key: `${category}_${q.id}`, id: String(q.id), category, source: 'anchor', text: q.text })
        }
      }
    }
  }
  if (isPlainObject(augmentByCategory)) {
    for (const [category, qs] of Object.entries(augmentByCategory)) {
      if (!Array.isArray(qs)) continue
      for (const q of qs) {
        if (q && q.id != null && typeof q.text === 'string' && q.text) {
          out.push({ key: `${category}_augment_${q.id}`, id: String(q.id), category, source: 'augment', text: q.text })
        }
      }
    }
  }
  return out
}

/** The API payload: the answers plus the reserved key (answered keys only). */
export function withQuestionText(answers, entries) {
  if (!isPlainObject(answers)) return answers
  const clean = stripQuestionText(answers)
  const list = mergeQuestionText(entries).filter((e) => e.key in clean)
  if (list.length === 0) return clean
  return { ...clean, [QUESTION_TEXT_KEY]: list }
}
